/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {gql} from '@apollo/client';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';
import {Strand} from 'constants/MolbioConstants';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {MatchSite, SequenceType} from 'constants/SequenceConstants';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {SequenceTypeQuery, SequenceTypeQueryVariables} from 'molbio/__generated/SequenceTypeQuery';
import CutSite from 'molbio/enzymes/backbone/CutSite';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import Slice from 'molbio/maps/linear/Slice';
import * as logging from 'util/Logging';
import {Optional} from 'util/Types';

type SequenceTypeData = SequenceTypeQuery['sequence'];

export const SEQUENCE_TYPE_QUERY = gql`
  query SequenceTypeQuery($sequenceId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    sequence(id: $sequenceId) {
      ... on SequenceNode {
        id
        sequenceType
        nucleotideType
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function postEditSelection(
  start: number,
  end: number | null | undefined,
  changeLength: number
): [number, number] {
  if (end != null && isWrap(start, end)) {
    return [0, changeLength];
  } else {
    return [start, start + changeLength];
  }
}

/**
 * Return whether the given range wraps around the origin of a sequence.
 * This should stay in sync with benchling/pybio/util.is_wrap
 */
function isWrap(start: number, end: number | null | undefined): boolean {
  const nowrap = end == null || start < end || end === 0;
  return !nowrap;
}

/**
 * Return whether the given base position is in the given range, taking wraps into account.
 */
function baseInRange(pos: number, start: number, end: number): boolean {
  if (start < end) {
    return start <= pos && pos < end;
  } else {
    return start <= pos || pos < end;
  }
}

/**
 * Return whether the given base position is within the range (start, end)
 */
function baseInExclusiveRange(pos: number, start: number, end: number): boolean {
  if (start < end) {
    return start < pos && pos < end;
  } else {
    return start < pos || pos < end;
  }
}

/**
 * Return whether every position in positionsToCheck is in the given range.
 */
function doesRangeInclude(start: number, end: number, positionsToCheck: Array<number>): boolean {
  // eslint-disable-next-line no-restricted-syntax
  return _.every(positionsToCheck, (pos) => baseInRange(pos, start, end));
}

/**
 * Calculate the minimum distance between two points on a sequence
 */
function distance(x: number, y: number, len: number, circular: boolean): number {
  const dist = Math.abs(x - y);
  if (!circular) {
    return dist;
  }

  const comp = len - dist;
  return Math.min(dist, comp);
}

function isRangeOverlap(s1: number, e1: number, s2: number, e2: number): boolean {
  return baseInRange(s2, s1, e1) || baseInRange(s1, s2, e2);
}

/**
 * Check that range is valid for a sequence feature (which is required to be non-zero in length.)
 */
function isValidRange(start: number, end: number, seqLength: number, circular: boolean): boolean {
  // This should stay in sync with benchling/pybio/util.is_valid_range
  return (
    0 <= start && start < seqLength && 0 <= end && end < seqLength && (start < end || circular || end === 0)
  );
}

/**
 * Return a list of [start, end] pairs with the overlaps between the given ranges.
 * Note that getRangeOverlap(5, 5, 4, 4) = [[5,4], [4,5]]
 */
function getRangeOverlap(s1: number, e1: number, s2: number, e2: number): Array<[number, number]> {
  let minLength;

  const results: Array<[number, number]> = [];

  function _getLinearRangeOverlap(start1: number, end1: number, start2: number, end2: number): void {
    const start = Math.max(start1, start2);
    const end = Math.min(end1, end2);
    if (start < end) {
      results.push([start, end]);
    }
  }

  if (s1 < e1 && s2 < e2) {
    _getLinearRangeOverlap(s1, e1, s2, e2);
  } else if (s1 >= e1 && s2 >= e2) {
    results.push([Math.max(s1, s2), Math.min(e1, e2)]);
    const minStart = Math.min(s1, s2);
    const maxEnd = Math.max(e1, e2);
    if (minStart < maxEnd) {
      results.push([minStart, maxEnd]);
    }
  } else if (s1 >= e1) {
    minLength = Math.max(s1, e2) + 1;
    _getLinearRangeOverlap(0, e1, s2, e2);
    _getLinearRangeOverlap(s1, minLength, s2, e2);
  } else {
    minLength = Math.max(s2, e1) + 1;
    _getLinearRangeOverlap(0, e2, s1, e1);
    _getLinearRangeOverlap(s2, minLength, s1, e1);
  }

  return results;
}

/**
 * Return whether first range pair is completely contained within second pair
 * Assumes first range pair does not cross the sequence origin
 */
function isRangeWithin(s1: number, e1: number, s2: number, e2: number): boolean {
  const rngs = getRangeOverlap(s1, e1, s2, e2);
  if (rngs.length === 1) {
    return rngs[0][0] === s1 && rngs[0][1] === e1;
  }
  return false;
}

/**
 * Return whether first range pair is completely contained within second pair
 * First range pair may cross the sequence origin
 */
function isCircularRangeWithin(s1: number, e1: number, s2: number, e2: number, seqLength: number): boolean {
  assertAndContinue(seqLength >= e2, 'Expected seqLength to be >= end of second range');
  const nonZeroE1 = e1 === 0 ? seqLength : e1;
  if (s1 >= nonZeroE1) {
    return isRangeWithin(s1, seqLength, s2, e2) && isRangeWithin(0, nonZeroE1, s2, e2);
  } else {
    return isRangeWithin(s1, nonZeroE1, s2, e2);
  }
}

/**
 * Return the length of a range of a sequence, taking wraps into account.
 * Note: When sequence length is 0, we always return 0 for the length.
 */
function length(start: number, end: number, seqLength: number): number {
  assertAndContinue(
    start != null && end != null && seqLength != null,
    'Some values in sequtil.length were not defined',
    {start, end, seqLength}
  );
  if (seqLength === 0) {
    return 0;
  }
  if (start < end) {
    return end - start;
  } else if (start === end) {
    return seqLength;
  } else {
    return (end - start + seqLength) % seqLength;
  }
}

function mod(n: number, m: number): number {
  assertAndContinue(n != null && Boolean(m), 'Bad mod values', {n, m});
  const result = n % m;
  if (result < 0) {
    return result + m;
  } else {
    return result;
  }
}

function endInRange(pos: number, start: number, end: number, seqLength: number): boolean {
  // Sanity check that pos should not be greater than length (this can happen in some weird edge cases)
  if (pos > seqLength) {
    return false;
  }
  return baseInRange(mod(pos - 1, seqLength), start, end);
}

// cases:
//   0 % m == 0
//   m - 1 % m == m - 1
//   m % m == m
//   m + 1 % m == 1
function modInclusive(n: number, m: number): number {
  if (n === m) {
    return n;
  } else {
    return mod(n, m);
  }
}

/**
 * This function translates a "model", 0-based value to a "display", 1-based value.
 * @param pos A 0-based number to be displayed to the user with adjustment.
 * @param startIndex An arbitrary, 1-based number to adjust the display of {@code pos}.
 */
function modelToDisplay(pos: number, startIndex: number): number;
function modelToDisplay(pos: Optional<number>, startIndex: number): Optional<number>;
function modelToDisplay(pos: Optional<number>, startIndex: number): Optional<number> {
  if (pos != null) {
    return pos + startIndex;
  } else {
    logging.warn('sequtil.modelToDisplay expects a non-null pos');
    return pos;
  }
}

/**
 * By convention, end=0 refers to the end of a sequence; however, for rendering purposes, we often want to
 * interpret this as a the numerical distance from the start.
 * @param end - End position of a feature (e.g. annotation)
 */
function modelEndToRender(end: number, len: number): number {
  if (end === 0) {
    return len;
  } else {
    return end;
  }
}

function looseModelToDisplay(pos: number, startIndex: number): number {
  if (startIndex == null) {
    logging.warn('sequtil.looseModelToDisplay expects a second parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  if (pos < 0) {
    return pos;
  } else {
    return pos + startIndex;
  }
}

function looseModelEndToDisplay(pos: number, startIndex: number): number {
  if (startIndex == null) {
    logging.warn('sequtil.looseModelEndToDisplay expects a second parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  if (pos <= 0) {
    return pos - 1;
  } else {
    return pos + startIndex - 1;
  }
}

function modelStartToDisplay(start: number, startIndex: Optional<number>): number;
function modelStartToDisplay(start: Optional<number>, startIndex: Optional<number>): Optional<number>;
function modelStartToDisplay(start: Optional<number>, startIndex: Optional<number>): Optional<number> {
  if (startIndex == null) {
    logging.warn('sequtil.modelStartToDisplay expects a second parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  return start == null ? start : modelToDisplay(start, startIndex);
}

function modelEndToDisplay(end: number, seqLength: number, startIndex: Optional<number>): number;
function modelEndToDisplay(
  end: Optional<number>,
  seqLength: number,
  startIndex: Optional<number>
): Optional<number>;
function modelEndToDisplay(
  end: Optional<number>,
  seqLength: number,
  startIndex: Optional<number>
): Optional<number> {
  if (seqLength == null) {
    logging.warn('sequtil.modelEndToDisplay expects a second parameter seqLength');
  }
  if (startIndex == null) {
    logging.warn('sequtil.modelEndToDisplay expects a third parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  if (end == null) {
    return end;
  } else if (end === 0) {
    return seqLength + startIndex - 1;
  } else {
    return end + startIndex - 1;
  }
}

function modelRangeToDisplay(
  start: number,
  end: number,
  seqLength: number,
  startIndex: number
): [number, number];
function modelRangeToDisplay(
  start: Optional<number>,
  end: Optional<number>,
  seqLength: number,
  startIndex: number
): [Optional<number>, Optional<number>];
function modelRangeToDisplay(
  start: Optional<number>,
  end: Optional<number>,
  seqLength: number,
  startIndex: number
): [Optional<number>, Optional<number>] {
  if (startIndex == null) {
    logging.warn('sequtil.modelRangeToDisplay expects a fourth parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  return [modelStartToDisplay(start, startIndex), modelEndToDisplay(end, seqLength, startIndex)];
}

function displayToModel(pos: number, startIndex: number): number {
  if (startIndex == null) {
    logging.warn('sequtil.displayToModel expects a second parameter startIndex');
  }
  return pos - startIndex;
}

function looseDisplayToModel(pos: number, startIndex: number): number {
  if (startIndex == null) {
    logging.warn('sequtil.looseDisplayToModel expects a second parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  if (pos > 0) {
    return pos - startIndex;
  } else if (pos === 0) {
    return 0;
  } else {
    // negative
    return pos;
  }
}

function looseDisplayEndToModel(pos: number, startIndex: number): number {
  if (startIndex == null) {
    logging.warn('sequtil.looseDisplayEndToModel expects a second parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  if (pos >= 0) {
    return pos - startIndex + 1;
  } else {
    // negative
    return pos + 1;
  }
}

function displayStartToModel(start: number, startIndex: number): number {
  if (startIndex == null) {
    logging.warn('sequtil.displayStartToModel expects a second parameter startIndex');
  }
  return start - startIndex;
}

function displayEndToModel(end: number, seqLength: number, startIndex: number): number {
  if (seqLength == null) {
    logging.warn('sequtil.displayEndToModel expects a second parameter seqLength');
  }
  if (startIndex == null) {
    logging.warn('sequtil.displayEndToModel expects a third parameter startIndex');
  }
  if (startIndex == null) {
    startIndex = 1;
  }
  if (end === seqLength + startIndex - 1) {
    return 0;
  } else {
    return end - startIndex + 1;
  }
}

function getMatchSiteInfo(cut: CutSite, sequenceLength: number): MatchSite {
  let matchEnd;
  let matchStart;
  const position = cut.get('position');
  const enzyme = cut.get('enzyme');
  const index = enzyme.get('matchingCut');
  const recognition = enzyme.get('matchingRegion');

  if (cut.strand() >= 0) {
    matchStart = position - index;
    if (matchStart < 0) {
      matchStart += sequenceLength;
    }
    matchEnd = matchStart + recognition.length;
    if (enzyme.doubleCutter()) {
      let otherEnd = cut.doubleCutterStart()!;
      if (enzyme.get('offset2') > 0) {
        otherEnd += enzyme.get('offset2');
      }
      matchEnd = Math.max(otherEnd, matchEnd);
    }
    matchEnd %= sequenceLength;
  } else {
    matchEnd = (position + enzyme.get('offset') + index) % sequenceLength;
    matchStart = matchEnd - recognition.length;
    if (enzyme.doubleCutter()) {
      let otherStart = cut.doubleCutterStart()!;
      if (enzyme.get('offset2') < 0) {
        otherStart += enzyme.get('offset2');
      }
      matchStart = Math.min(matchStart, otherStart);
    }
    if (matchStart < 0) {
      matchStart += sequenceLength;
    }
  }
  return {
    matchStart,
    matchEnd,
    enzymeName: (enzyme as Enzyme).get('name'),
    recognitionSite: (enzyme as Enzyme).get('seq'),
    enzymeId: (enzyme as Enzyme).get('id'),
  };
}

function setCutsiteData(
  sequence: Sequence,
  sliceMap: {[key: number]: Slice} | Array<Slice>,
  maxBases: number
): void {
  const sliceCuts: {[key: number]: Array<CutSite>} = {};
  for (const i of Object.keys(sliceMap)) {
    sliceCuts[parseInt(i, 10)] = [];
  }

  const cutsites = sequence.get('cutsites');

  // Divide cutsites into the slices.
  for (const cut of Array.from(cutsites.models)) {
    // Compute matchStart and matchEnd for the cutsite.
    const {matchStart, matchEnd} = getMatchSiteInfo(cut, sequence.length());

    // Determine which slices the cut belongs to.
    const sliceI = Math.floor(matchStart / maxBases);
    const sliceJ = Math.floor(matchEnd / maxBases);

    // Augment cut with slice data.
    if (sliceI in sliceCuts) {
      sliceCuts[sliceI].push(cut);
    }

    if (sliceJ in sliceCuts && sliceJ !== sliceI) {
      sliceCuts[sliceJ].push(cut);
    }
  }

  for (const i of Object.keys(sliceCuts)) {
    const key = parseInt(i, 10);
    sliceCuts[key] = _.sortBy(sliceCuts[key], (cut: CutSite) => cut.name().length);
    sliceMap[key].set('cutData', sliceCuts[key]);
  }
}

/**
 * Return whether a selection on a sequence traverses the full sequence
 * and should be considered a full circular selection
 */
function fullCircular(
  circular: boolean,
  selStart: number,
  selEnd: number | null,
  seqLength: number
): boolean {
  return circular && (selStart === selEnd || (selStart === 0 && selEnd === seqLength));
}

function residueSlice(residues: string, start: number, end?: number, allowCircular?: boolean): string {
  if (end != null && start < end) {
    return residues.slice(start, end);
  } else {
    return residues.slice(start) + residues.slice(0, end);
  }
}

/**
 * Return the binding position for a primer given a sequence length, accounting for strand.
 */
function primerBindPosition(
  primerStrand: Strand,
  primerStart: number,
  primerEnd: number,
  sequenceLength: number
): number {
  if (sequenceLength === 0) {
    return NaN;
  } else if (primerStrand === 1) {
    return mod(primerEnd - 1, sequenceLength);
  }
  return primerStart;
}

function isOligoSubtype(subtype: SchemalessBulkImportSupportedOligoAwareTypes | null | undefined): boolean {
  return subtype === ModelTypeConstants.DNA_OLIGO || subtype === ModelTypeConstants.RNA_OLIGO;
}

function subtypeSupportsChemicalMods(subtype: SchemalessBulkImportSupportedOligoAwareTypes): boolean {
  return (
    subtype === ModelTypeConstants.DNA_OLIGO ||
    subtype === ModelTypeConstants.RNA_OLIGO ||
    subtype === ModelTypeConstants.RNA_SEQUENCE
  );
}

/**
 * Get the nucleotide type and sequence type for a given model SequenceSubtype.
 */
export const SEQUENCE_SUBTYPE_DETAILS: {
  [subtype in SequenceSubtype]: {nucleotideType: NucleotideType; sequenceType: SequenceType};
} = {
  [ModelTypeConstants.SEQUENCE]: {
    nucleotideType: NucleotideType.DNA,
    sequenceType: SequenceType.DOUBLE_STRANDED,
  },
  [ModelTypeConstants.RNA_SEQUENCE]: {
    nucleotideType: NucleotideType.RNA,
    sequenceType: SequenceType.SINGLE_STRANDED,
  },
  [ModelTypeConstants.DNA_OLIGO]: {nucleotideType: NucleotideType.DNA, sequenceType: SequenceType.OLIGO},
  [ModelTypeConstants.RNA_OLIGO]: {nucleotideType: NucleotideType.RNA, sequenceType: SequenceType.OLIGO},
};

/**
 * Asynchronously evaluate the nucleotide type and sequence type for a given sequence ID and eval func.
 *
 * Operation is asynchronous because we use GQL to fetch the sequence details.
 * Warning: Do not use this function in bulk as it makes repeated GQL calls (see CUST-9100)
 */
export async function evaluateSequenceType<T>(
  sequenceId: string,
  evaluate: (sequenceInfo: SequenceTypeData) => T
): Promise<T> {
  // Circular import that only manifests in snapshot integration tests :lolsob:
  const {doQuerySafe} = require('graphql-client/ApolloWrappers');
  // @ts-expect-error
  const {sequence} = await doQuerySafe<SequenceTypeQuery, SequenceTypeQueryVariables>(
    {query: SEQUENCE_TYPE_QUERY},
    {variables: {sequenceId}}
  );
  return evaluate(sequence);
}

export function getSequenceSubtype(
  sequenceType: SequenceType,
  nucleotideType: NucleotideType
): SequenceSubtype {
  if (sequenceType === SequenceType.DOUBLE_STRANDED) {
    return ModelTypeConstants.SEQUENCE;
  } else if (sequenceType === SequenceType.SINGLE_STRANDED) {
    return ModelTypeConstants.RNA_SEQUENCE;
  } else if (nucleotideType === NucleotideType.DNA) {
    return ModelTypeConstants.DNA_OLIGO;
  } else {
    return ModelTypeConstants.RNA_OLIGO;
  }
}

/**
 * Get the new position on a sequence after being reindexed
 */
export function getReindexedPosition(pos: number, reindexPos: number, seqLength: number): number {
  return (seqLength + pos - reindexPos) % seqLength;
}

export {
  baseInRange,
  baseInExclusiveRange,
  residueSlice,
  displayEndToModel,
  displayStartToModel,
  displayToModel,
  distance,
  doesRangeInclude,
  endInRange,
  fullCircular,
  getRangeOverlap,
  getMatchSiteInfo,
  isOligoSubtype,
  isRangeOverlap,
  isRangeWithin,
  isCircularRangeWithin,
  isValidRange,
  isWrap,
  length,
  looseDisplayEndToModel,
  looseDisplayToModel,
  looseModelEndToDisplay,
  looseModelToDisplay,
  mod,
  modelEndToDisplay,
  modelEndToRender,
  modelRangeToDisplay,
  modelStartToDisplay,
  modelToDisplay,
  modInclusive,
  postEditSelection,
  primerBindPosition,
  setCutsiteData,
  subtypeSupportsChemicalMods,
};
